import React, { FC, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import {
  DataGridPremium,
  GridColDef,
  GridFilterModel,
  GridRowParams,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import { FinancialFrameTableProps } from './FinancialFrameTableProps';
import {
  applyFinancialFramesFilter,
  getFilterValues,
  hasSameValues,
  returnFinancialFramesColumnGroupingModel,
  returnFinancialFramesTableColumns,
  returnFinancialFramesTableSx,
} from './utils';
import useTranslation from '../../hooks/useTranslation';
import IngkaDropdownMenu from '../IngkaDropdownMenu';
import FinancialFrameToolbar from './FinancialFrameToolbar';
import { ListBoxFooter } from '@ingka/list-box';
import Button from '@ingka/button';

const FinancialFrameTable: FC<FinancialFrameTableProps> = ({
  financialFrames,
  onRowClick,
  editAllowed,
  fyOptions,
  countryOptions,
  selectedFYs,
  selectedCountries,
  onFyChange,
  onCountryChange,
}) => {
  const t = useTranslation();
  const apiRef = useGridApiRef();
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(
    null,
  );

  useEffect(() => {
    applyFinancialFramesFilter(apiRef, 'fiscalYear', 'ff-fy', selectedFYs);
  }, [selectedFYs, apiRef]);

  useEffect(() => {
    applyFinancialFramesFilter(
      apiRef,
      'countryId',
      'ff-country',
      selectedCountries,
    );
  }, [selectedCountries, apiRef]);

  // Keeps the dropdowns in sync when filters are changed from the filter panel.
  const onFilterModelChange = (model: GridFilterModel) => {
    const fys = getFilterValues(model.items, 'fiscalYear');
    if (!hasSameValues(fys, selectedFYs)) onFyChange(fys);
    const countries = getFilterValues(model.items, 'countryId');
    if (!hasSameValues(countries, selectedCountries)) {
      onCountryChange(countries);
    }
  };

  const onFyToggle = (fys: string[]) => onFyChange(fys);

  const onCountryToggle = (countryCodes: string[]) =>
    onCountryChange(countryCodes);

  const initialFilterItems = [
    ...(selectedFYs.length
      ? [
          {
            field: 'fiscalYear',
            operator: 'isAnyOf',
            value: selectedFYs,
            id: 'ff-fy',
          },
        ]
      : []),
    ...(selectedCountries.length
      ? [
          {
            field: 'countryId',
            operator: 'isAnyOf',
            value: selectedCountries,
            id: 'ff-country',
          },
        ]
      : []),
  ];

  const columns = returnFinancialFramesTableColumns(t, countryOptions);
  const tableSx = returnFinancialFramesTableSx(editAllowed);

  return (
    <Box
      sx={{
        width: '100%',
        height: 'calc(100vh - 420px)',
        minHeight: 320,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <DataGridPremium
        apiRef={apiRef}
        disableRowSelectionOnClick
        hideFooter
        sx={tableSx}
        slots={{ toolbar: FinancialFrameToolbar }}
        onFilterModelChange={onFilterModelChange}
        slotProps={{
          panel: {
            anchorEl: filterAnchorEl,
            placement: 'bottom-start',
          },
          toolbar: {
            setFilterAnchorEl,
            children: (
              <>
                <IngkaDropdownMenu
                  id="fin-frames-fy-filter"
                  label={t('financialFrames.columns.fiscalYear')}
                  options={fyOptions}
                  selectedValues={selectedFYs}
                  onToggle={onFyToggle as (fys: string[]) => void}
                  defaultToggle
                />
                {countryOptions.length > 1 && (
                  <IngkaDropdownMenu
                    id="fin-frames-country-filter"
                    label={t('financialFrames.columns.country')}
                    options={countryOptions}
                    selectedValues={selectedCountries}
                    onToggle={onCountryToggle as (countries: string[]) => void}
                    defaultToggle
                    addSearch
                    limitedHeight
                    footer={
                      <ListBoxFooter>
                        <Button
                          fluid
                          size={'small'}
                          type="secondary"
                          text="Select None"
                          onClick={() => onCountryChange([])}
                        />
                        <Button
                          fluid
                          size={'small'}
                          type="secondary"
                          text="Select All"
                          onClick={() =>
                            onCountryChange(
                              countryOptions.map((option) => option.value),
                            )
                          }
                        />
                      </ListBoxFooter>
                    }
                  />
                )}
              </>
            ),
          },
        }}
        initialState={{
          pinnedColumns: { left: ['countryName', 'fiscalYear'] },
          filter: { filterModel: { items: initialFilterItems } },
          columns: { columnVisibilityModel: { countryId: false } },
          sorting: {
            sortModel: [
              { field: 'countryName', sort: 'asc' },
              { field: 'fiscalYear', sort: 'asc' },
            ],
          },
        }}
        columnGroupingModel={returnFinancialFramesColumnGroupingModel(t)}
        columns={columns as GridColDef[]}
        rows={financialFrames}
        onRowClick={(params: GridRowParams) => {
          if (!editAllowed) return;
          onRowClick(params.row.id);
        }}
      />
    </Box>
  );
};

export default FinancialFrameTable;
